import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, KeyRound, RefreshCw, LogOut, Star, Users, Download, Sparkles, DollarSign, CheckCircle2, Eye } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BrandMark, BrandWordmark } from "./LandingPage";

const TOKEN_KEY = "bankable_admin_token_v1";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [draft, setDraft] = useState("");
  const [data, setData] = useState(null);
  const [waitlist, setWaitlist] = useState(null);
  const [payments, setPayments] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load(t, { probe = false } = {}) {
    setLoading(true);
    setError("");
    try {
      const [insights, wl, py, tr] = await Promise.all([
        api.get("/admin/insights", { params: { token: t, ...(probe ? { probe: 1 } : {}) } }),
        api.get("/admin/waitlist", { params: { token: t, product: "cfo_books" } }),
        api.get("/admin/payments", { params: { token: t } }),
        api.get("/admin/traffic", { params: { token: t } }),
      ]);
      setData(insights.data);
      setWaitlist(wl.data);
      setPayments(py.data);
      setTraffic(tr.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not load admin insights.";
      setError(msg);
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleAuth() {
    if (!draft.trim()) {
      toast.error("Enter the admin token.");
      return;
    }
    const t = draft.trim();
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
    load(t);
  }

  function handleSignOut() {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
    setWaitlist(null);
    setPayments(null);
    setDraft("");
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] bg-grain flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-[#E5E0D8] rounded-2xl p-8 fade-up">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={18} strokeWidth={1.5} className="text-[#C26D5C]" />
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">CFO Grade admin</p>
          </div>
          <h1 className="font-serif-display text-3xl text-[#1F382E] leading-tight">
            Operator-only dashboard.
          </h1>
          <p className="text-sm text-[#5C665D] mt-3 leading-relaxed">
            Enter the <code className="bg-[#F7F5F0] px-1.5 py-0.5 rounded">ADMIN_TOKEN</code> from
            your backend <code className="bg-[#F7F5F0] px-1.5 py-0.5 rounded">.env</code>.
          </p>
          <div className="mt-6">
            <Label className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">Token</Label>
            <Input
              type="password"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAuth()}
              placeholder="Paste your admin token"
              data-testid="admin-token-input"
              className="mt-2 bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E]"
            />
            {error && (
              <p className="text-xs text-[#A84632] mt-2" data-testid="admin-error">
                {error}
              </p>
            )}
            <Button
              onClick={handleAuth}
              disabled={loading}
              data-testid="admin-auth-btn"
              className="w-full mt-4 bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md transition-all duration-300"
            >
              {loading ? "Verifying…" : "Unlock"}
            </Button>
            <Link to="/" className="block text-center text-xs text-[#5C665D] mt-4 hover:text-[#1F382E]">
              ← Back to site
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <p className="text-[#5C665D]">Loading insights…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] bg-grain relative">
      <header className="max-w-7xl mx-auto px-6 sm:px-10 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <BrandWordmark size="md" />
          <span className="ml-3 text-xs uppercase tracking-[0.22em] text-[#C26D5C]">admin</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/">
            <Button variant="ghost" className="text-[#5C665D] hover:bg-[#D4C9B6]/40">
              <ArrowLeft size={14} strokeWidth={1.5} className="mr-2" />
              Site
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => load(token, { probe: true })}
            disabled={loading}
            className="border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0]"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={14} strokeWidth={1.5} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
          <Button
            variant="ghost"
            onClick={handleSignOut}
            className="text-[#5C665D] hover:bg-[#D4C9B6]/40"
            data-testid="admin-signout-btn"
          >
            <LogOut size={14} strokeWidth={1.5} className="mr-2" />
            Sign out
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-10 sm:py-14 space-y-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C665D]">Operator dashboard</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl text-[#1F382E] leading-tight tracking-tight mt-3">
              Your moat, in real time.
            </h1>
          </div>
          {data.last_reset && (
            <div
              className="text-right shrink-0"
              data-testid="last-reset-badge"
              title={`Factory reset ran at ${new Date(data.last_reset.ts).toLocaleString()}`}
            >
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#5C665D]">
                Stats reset
              </p>
              <p className="font-serif-display text-lg text-[#1F382E] mt-0.5">
                {timeAgo(new Date(data.last_reset.ts))}
              </p>
              <p className="text-[11px] text-[#5C665D] mt-0.5">
                {fmtNumber(data.last_reset.total_deleted)} test rows cleared
              </p>
            </div>
          )}
        </div>

        <Totals totals={data.totals} ai={data.ai_status} />

        <TrafficPanel traffic={traffic} />

        <PaymentsPanel payments={payments} token={token} onReload={() => load(token)} />

        <WaitlistPanel waitlist={waitlist} />

        <div className="grid lg:grid-cols-2 gap-6">
          <NearThreshold items={data.near_threshold} minN={data.min_n} />
          <ContributionsTable items={data.contribution_groups} minN={data.min_n} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <FeedbackDist dist={data.feedback_distribution} total={data.totals.feedback} />
          <KvCard title="Reports by audience" data={data.reports_by_audience} />
          <KvCard title="Reports by region" data={data.reports_by_region} />
        </div>

        <RecentFeedback items={data.recent_feedback} totalCount={data.totals.feedback} token={token} />

        <FactoryResetPanel token={token} onReload={() => load(token)} />
      </main>
    </div>
  );
}

function filterLabel(f) {
  if (f === "low") return "1-2★";
  if (f === "with-comment") return "Has a comment";
  return "All";
}

function FactoryResetPanel({ token, onReload }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  async function runPreview() {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.delete("/admin/factory-reset", {
        params: { token, dry_run: 1 },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  }

  async function runReset() {
    if (!preview) {
      toast.error("Run preview first.");
      return;
    }
    const total = Object.values(preview.matched || {}).reduce((a, b) => a + b, 0);
    const ok = window.confirm(
      `FACTORY RESET — wipe ${total} test/seed rows across 7 collections?\n\n` +
        `This will remove all build-time test data so the dashboard shows real customer activity only.\n\n` +
        `Live (cs_live_*) payments and non-seed waitlist signups are PRESERVED.\n\n` +
        `This cannot be undone. Continue?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.delete("/admin/factory-reset", {
        params: { token, dry_run: 0, confirm: "ERASE" },
      });
      toast.success(`Wiped ${r.data?.total_deleted || 0} test/seed rows.`);
      setPreview(null);
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  }

  const matched = preview?.matched || {};
  const totalMatched = Object.values(matched).reduce((a, b) => a + b, 0);

  return (
    <Card className="bg-white border-[#8B2C2C]/20 shadow-none" data-testid="factory-reset-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#8B2C2C]">
              Maintenance · destructive
            </CardDescription>
            <CardTitle className="font-serif-display text-2xl text-[#1F382E] mt-1">
              Factory reset · clear test data
            </CardTitle>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-[#1A1A1A] leading-relaxed max-w-2xl">
          Wipes pageviews, feedback, reports, contributions, sandbox payments, and obvious-seed
          waitlist rows so the dashboard reflects real customer activity from this moment forward.{" "}
          <strong>Live payments and real waitlist signups are preserved.</strong>
        </p>

        {preview && (
          <div
            className="mt-5 rounded-xl border border-[#E5E0D8] bg-[#F7F5F0] p-4"
            data-testid="factory-reset-preview"
          >
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
              Will delete
            </p>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
              {Object.entries(matched).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <span className="text-[#5C665D]">{k}</span>
                  <span className="font-medium text-[#1F382E] tabular-nums">
                    {fmtNumber(v)}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[#E5E0D8] flex justify-between text-sm">
              <span className="font-medium text-[#1A1A1A]">Total rows</span>
              <span className="font-semibold text-[#1F382E] tabular-nums">
                {fmtNumber(totalMatched)}
              </span>
            </div>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={runPreview}
            disabled={busy}
            className="border-[#E5E0D8] text-[#1F382E] hover:bg-[#F7F5F0]"
            data-testid="factory-reset-preview-btn"
          >
            {busy ? "Working…" : "Preview what would be wiped"}
          </Button>
          <Button
            size="sm"
            onClick={runReset}
            disabled={busy || !preview || totalMatched === 0}
            className="bg-[#8B2C2C] hover:bg-[#751f1f] text-white disabled:opacity-50"
            data-testid="factory-reset-go-btn"
          >
            {preview && totalMatched
              ? `Wipe ${fmtNumber(totalMatched)} rows`
              : "Wipe test data"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Totals({ totals, ai }) {
  const items = [
    { label: "Reports run", value: totals.reports },
    { label: "Contributions", value: totals.contributions },
    { label: "Feedback received", value: totals.feedback },
    { label: "Acceptances logged", value: totals.acceptances },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {items.map((it) => (
        <div
          key={it.label}
          className="bg-white border border-[#E5E0D8] rounded-xl p-5"
        >
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            {it.label}
          </p>
          <p className="font-serif-display text-3xl text-[#1F382E] tabular-nums mt-2 leading-none">
            {Number(it.value).toLocaleString()}
          </p>
        </div>
      ))}
      {ai && (
        <div className="bg-white border border-[#E5E0D8] rounded-xl p-5 col-span-2 lg:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">AI key status</p>
          <div className="flex items-center gap-2 mt-2">
            <span
              className="h-2 w-2 rounded-full"
              style={{
                background:
                  ai.state === "ok" ? "#2C5C43" :
                  ai.state === "budget_exceeded" ? "#E2A03F" : "#A84632",
              }}
            />
            <span className="text-sm text-[#1A1A1A]">
              {ai.state === "ok" && "Operational"}
              {ai.state === "budget_exceeded" && "Budget exhausted — top up Universal Key"}
              {ai.state === "error" && "Error"}
            </span>
            {ai.checked_at && (
              <span className="text-xs text-[#5C665D] ml-auto">
                last checked {new Date(ai.checked_at).toLocaleString()}
              </span>
            )}
          </div>
          {ai.last_error && (
            <p className="text-xs text-[#A84632] mt-2 font-mono-num">
              {ai.last_error.slice(0, 200)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function NearThreshold({ items, minN }) {
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
          Almost unlocked
        </CardDescription>
        <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
          Industries closest to community medians.
        </CardTitle>
        <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
          Each segment unlocks live community medians at <strong>n = {minN}</strong>. Push for
          contributions in these first.
        </p>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-[#5C665D]">
            No segments in flight yet — every contribution starts a new bucket.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.slice(0, 8).map((it, i) => (
              <li
                key={i}
                className="flex items-center justify-between border-b border-[#F0EDE5] pb-3 last:border-b-0 last:pb-0"
              >
                <div>
                  <p className="text-sm text-[#1A1A1A] capitalize">
                    {it.industry_key.replace(/_/g, " ")}{" "}
                    <span className="text-[#5C665D]">· {it.region}</span>
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    {Array.from({ length: minN }).map((_, idx) => (
                      <span
                        key={idx}
                        className="h-1.5 w-6 rounded-full"
                        style={{
                          background: idx < it.n ? "#1F382E" : "#E5E0D8",
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-serif-display text-2xl text-[#1F382E] tabular-nums leading-none">
                    {it.n}
                  </p>
                  <p className="text-[11px] uppercase tracking-[0.16em] text-[#C26D5C] mt-1">
                    {it.needed} to go
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function ContributionsTable({ items, minN }) {
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
          Contribution map
        </CardDescription>
        <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
          By industry × region.
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-[#5C665D]">No contributions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                  <th className="py-2.5 pr-4 font-medium">Industry</th>
                  <th className="py-2.5 pr-4 font-medium">Region</th>
                  <th className="py-2.5 pr-4 font-medium text-right">n</th>
                  <th className="py-2.5 pr-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 20).map((it, i) => (
                  <tr
                    key={i}
                    className="border-b border-[#F0EDE5] last:border-b-0"
                  >
                    <td className="py-2.5 pr-4 text-[#1A1A1A] capitalize">
                      {it.industry_key.replace(/_/g, " ")}
                    </td>
                    <td className="py-2.5 pr-4 text-[#5C665D]">{it.region}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums text-[#1F382E]">
                      {it.n}
                    </td>
                    <td className="py-2.5 pr-4">
                      {it.unlocked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2C5C43]/10 text-[#2C5C43] text-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#2C5C43]" />
                          medians live
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E2A03F]/15 text-[#9c6b1d] text-xs">
                          <span className="h-1.5 w-1.5 rounded-full bg-[#E2A03F]" />
                          {minN - it.n} to go
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackDist({ dist, total }) {
  const max = useMemo(() => {
    return Math.max(1, ...Object.values(dist || {}).map(Number));
  }, [dist]);
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
          Feedback distribution
        </CardDescription>
        <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
          {total} ratings
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((s) => {
            const v = Number(dist?.[String(s)] || 0);
            return (
              <div key={s} className="flex items-center gap-3 text-sm">
                <span className="flex items-center gap-1 w-8 text-[#5C665D]">
                  {s}
                  <Star size={11} strokeWidth={1.5} className="fill-[#C26D5C] text-[#C26D5C]" />
                </span>
                <div className="flex-1 h-2 bg-[#F7F5F0] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1F382E] rounded-full"
                    style={{ width: `${(v / max) * 100}%` }}
                  />
                </div>
                <span className="w-10 text-right tabular-nums text-[#1F382E]">{v}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function KvCard({ title, data }) {
  const entries = Object.entries(data || {});
  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
          {title}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-sm text-[#5C665D]">No data yet.</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {entries
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <li
                  key={k}
                  className="flex items-center justify-between border-b border-[#F0EDE5] last:border-b-0 pb-1.5"
                >
                  <span className="text-[#1A1A1A] capitalize">{k}</span>
                  <span className="tabular-nums text-[#1F382E]">{v}</span>
                </li>
              ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function WaitlistPanel({ waitlist }) {
  if (!waitlist) {
    return (
      <div className="bg-white border border-[#E5E0D8] rounded-2xl p-7" data-testid="waitlist-panel">
        <p className="text-sm text-[#5C665D]">Loading waitlist data…</p>
      </div>
    );
  }

  const { total, target, progress_pct, by_business_type, items, reserved_count, reserved_pending } = waitlist;
  const remaining = Math.max(0, target - total);
  const greenLight = total >= target;
  const softSignal = total >= 25;
  const reservedItems = (items || []).filter((it) => it.preordered);
  const paidCount = reserved_count ?? reservedItems.length;
  const pendingCount = reserved_pending ?? 0;

  function downloadCSV() {
    const header = ["position", "email", "business_type", "notes", "submitted_at", "reserved", "reserved_at"];
    const rows = (items || []).map((it) => [
      it.position ?? "",
      (it.email || "").replace(/"/g, '""'),
      (it.business_type || "").replace(/"/g, '""'),
      (it.notes || "").replace(/[\r\n"]+/g, " ").trim(),
      it.submitted_at || "",
      it.preordered ? "yes" : (it.preorder_pending ? "pending" : "no"),
      it.preordered_at || "",
    ]);
    const csv = [header, ...rows]
      .map((r) => r.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cfo-books-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid="waitlist-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 flex-wrap">
        <div>
          <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D] flex items-center gap-2">
            <Users size={13} strokeWidth={1.5} />
            CFO Books waitlist
          </CardDescription>
          <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
            {greenLight ? (
              <span className="flex items-center gap-2">
                <Sparkles size={18} strokeWidth={1.5} className="text-[#C26D5C]" />
                Green light — build it.
              </span>
            ) : softSignal ? (
              "Soft signal — talk to your list."
            ) : (
              "Building demand."
            )}
          </CardTitle>
          <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
            {greenLight
              ? `You have ${total} signups. Time to ship the bank-statement engine.`
              : `${remaining} more signups to hit the ${target}-person green light.`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={downloadCSV}
            disabled={!items || items.length === 0}
            className="border-[#1F382E]/20 text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0]"
            data-testid="waitlist-csv-btn"
          >
            <Download size={14} strokeWidth={1.5} className="mr-2" />
            CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Milestone celebration banner */}
        {(softSignal || greenLight) && (
          <div
            className={`-mt-2 mb-6 p-4 rounded-xl border-2 ${
              greenLight
                ? "bg-[#2C5C43]/8 border-[#2C5C43] text-[#1F382E]"
                : "bg-[#C26D5C]/8 border-[#C26D5C] text-[#1F382E]"
            }`}
            data-testid={greenLight ? "milestone-greenlight-banner" : "milestone-softsignal-banner"}
          >
            <div className="flex items-start gap-3">
              <Sparkles
                size={20}
                strokeWidth={1.5}
                className={greenLight ? "text-[#2C5C43]" : "text-[#C26D5C]"}
              />
              <div>
                <p className="font-serif-display text-lg leading-tight">
                  {greenLight
                    ? `You crossed ${target}. Time to ship the bank-statement engine.`
                    : `You crossed 25. Email your list and validate the pitch.`}
                </p>
                <p className="text-sm text-[#5C665D] mt-1 leading-relaxed">
                  {greenLight
                    ? "Real demand. Schedule the build session and start with Chase + BoA + Wells Fargo bank-statement parsers."
                    : "Send a short personal email to your top 25 signups — ask them one specific question about their bookkeeping pain. The answers shape what we build."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        <div className="space-y-1.5 mb-6">
          <div className="flex items-end justify-between">
            <span className="font-serif-display text-4xl text-[#1F382E] tabular-nums leading-none">
              {total}
            </span>
            <span className="text-xs uppercase tracking-[0.16em] text-[#5C665D]">
              of {target} target
            </span>
          </div>
          <div className="h-2 bg-[#F7F5F0] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.min(100, progress_pct)}%`,
                background: greenLight ? "#2C5C43" : softSignal ? "#C26D5C" : "#1F382E",
              }}
            />
          </div>
          <div className="flex justify-between text-[10px] uppercase tracking-[0.18em] text-[#5C665D] mt-2">
            <span>0</span>
            <span>25 — soft signal</span>
            <span>50 — green light</span>
          </div>
        </div>

        {/* By business type */}
        {Object.keys(by_business_type || {}).length > 0 && (
          <div className="mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] mb-2">
              Top business types
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(by_business_type).map(([k, v]) => (
                <span
                  key={k}
                  className="inline-flex items-center gap-1.5 bg-[#F7F5F0] border border-[#E5E0D8] rounded-full px-3 py-1 text-xs"
                  data-testid={`waitlist-bt-${k.replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="capitalize text-[#1A1A1A]">{k}</span>
                  <span className="text-[#5C665D] tabular-nums">· {v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Paid reservers — real money down */}
        <div
          className="mb-6 rounded-xl border-2 border-[#2C5C43]/40 bg-[#2C5C43]/5 p-4 sm:p-5"
          data-testid="reserved-paid-panel"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#2C5C43] font-medium flex items-center gap-1.5">
                <CheckCircle2 size={12} strokeWidth={2} /> Founding members · paid reservations
              </p>
              <div className="flex items-baseline gap-4 mt-1 flex-wrap">
                <p className="font-serif-display text-3xl text-[#1F382E] tabular-nums leading-none">
                  {paidCount}
                </p>
                {reservedItems.length > 0 && (
                  <div
                    className="text-[12.5px] text-[#1A1A1A] min-w-0"
                    data-testid="reserved-quick-emails"
                  >
                    <span className="text-[#5C665D]">Who: </span>
                    {reservedItems.slice(0, 3).map((it, i) => (
                      <span key={it.email}>
                        <a
                          href={`mailto:${it.email}`}
                          className="font-medium text-[#1F382E] underline underline-offset-2 hover:text-[#2C5C43]"
                        >
                          {it.email}
                        </a>
                        {i < Math.min(reservedItems.length, 3) - 1 ? ", " : ""}
                      </span>
                    ))}
                    {reservedItems.length > 3 && (
                      <span className="text-[#5C665D]"> +{reservedItems.length - 3} more below</span>
                    )}
                  </div>
                )}
              </div>
              <p className="text-[12.5px] text-[#5C665D] mt-1.5 leading-relaxed">
                Real $1 deposits on file via Stripe. These customers have locked in $79/mo for 12 months.
                {pendingCount > 0 && (
                  <>
                    {" "}
                    <span className="text-[#9c6b1d]">+{pendingCount} pending</span> (checkout started, not confirmed).
                  </>
                )}
              </p>
            </div>
          </div>

          {reservedItems.length > 0 ? (
            <div className="mt-4 overflow-x-auto -mx-1">
              <table className="w-full text-sm" data-testid="reserved-emails-table">
                <thead>
                  <tr className="text-left border-b border-[#2C5C43]/20 text-[#5C665D]">
                    <th className="py-2 px-2 font-medium text-right">#</th>
                    <th className="py-2 px-2 font-medium">Email</th>
                    <th className="py-2 px-2 font-medium">Business type</th>
                    <th className="py-2 px-2 font-medium">Reserved on</th>
                  </tr>
                </thead>
                <tbody>
                  {reservedItems.map((it) => (
                    <tr
                      key={it.id}
                      className="border-b border-[#2C5C43]/10 last:border-b-0"
                      data-testid={`reserved-row-${it.email}`}
                    >
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E]">{it.position}</td>
                      <td className="py-2 px-2 text-[#1A1A1A]">
                        <a
                          href={`mailto:${it.email}`}
                          className="underline underline-offset-2 hover:text-[#2C5C43]"
                        >
                          {it.email}
                        </a>
                      </td>
                      <td className="py-2 px-2 text-[#5C665D] capitalize">
                        {it.business_type || "—"}
                      </td>
                      <td className="py-2 px-2 text-[#5C665D] text-xs whitespace-nowrap">
                        {it.preordered_at ? new Date(it.preordered_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-[12.5px] text-[#5C665D] mt-3 leading-relaxed">
              No paid reservations yet. Direct people to{" "}
              <code className="bg-white px-1.5 py-0.5 rounded border border-[#E5E0D8]">/cfo-books</code>{" "}
              — the page now requires a $1 Stripe deposit to reserve a spot.
            </p>
          )}
        </div>

        {/* Recent signups table */}
        <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D] mb-2">
          All signups (first 25)
        </p>
        {items && items.length > 0 ? (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                  <th className="py-2.5 px-2 font-medium text-right">#</th>
                  <th className="py-2.5 px-2 font-medium">Email</th>
                  <th className="py-2.5 px-2 font-medium">Status</th>
                  <th className="py-2.5 px-2 font-medium">Business type</th>
                  <th className="py-2.5 px-2 font-medium">Feedback</th>
                  <th className="py-2.5 px-2 font-medium">When</th>
                </tr>
              </thead>
              <tbody>
                {items.slice(0, 25).map((it) => (
                  <tr key={it.id} className="border-b border-[#F0EDE5] last:border-b-0">
                    <td className="py-2 px-2 text-right tabular-nums text-[#1F382E]">
                      {it.position}
                    </td>
                    <td className="py-2 px-2 text-[#1A1A1A]">
                      <a
                        href={`mailto:${it.email}`}
                        className="underline underline-offset-2 hover:text-[#C26D5C]"
                      >
                        {it.email}
                      </a>
                    </td>
                    <td className="py-2 px-2">
                      {it.preordered ? (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-[#2C5C43] bg-[#2C5C43]/10 border border-[#2C5C43]/25 rounded-full px-2 py-0.5"
                          data-testid={`status-paid-${it.email}`}
                        >
                          <CheckCircle2 size={10} strokeWidth={2} /> Paid
                        </span>
                      ) : it.preorder_pending ? (
                        <span className="inline-flex items-center text-[10px] uppercase tracking-[0.16em] text-[#9c6b1d] bg-[#9c6b1d]/10 border border-[#9c6b1d]/25 rounded-full px-2 py-0.5">
                          Pending
                        </span>
                      ) : (
                        <span className="text-[11px] text-[#5C665D]">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-[#5C665D] capitalize">
                      {it.business_type || "—"}
                    </td>
                    <td className="py-2 px-2 text-[#1A1A1A] max-w-md">
                      {it.notes ? (
                        <span className="line-clamp-2">{it.notes}</span>
                      ) : (
                        <span className="text-[#5C665D]">—</span>
                      )}
                    </td>
                    <td className="py-2 px-2 text-[#5C665D] text-xs whitespace-nowrap">
                      {it.submitted_at ? new Date(it.submitted_at).toLocaleDateString() : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-[#5C665D]">
            No signups yet. Drive traffic to <code className="bg-[#F7F5F0] px-1.5 py-0.5 rounded">/cfo-books</code>.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function RecentFeedback({ items, totalCount, token }) {
  const [allItems, setAllItems] = useState(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const [filter, setFilter] = useState("all"); // all | low | with-comment

  const shown = allItems || items || [];
  const filtered = shown.filter((f) => {
    if (filter === "low") return (f.rating || 0) <= 2;
    if (filter === "with-comment") return (f.comment || "").trim().length > 0;
    return true;
  });

  async function loadAll() {
    if (allItems || !token) return;
    setLoadingAll(true);
    try {
      const r = await api.get("/admin/feedback", {
        params: { token, limit: 1000 },
      });
      setAllItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load all feedback.");
    } finally {
      setLoadingAll(false);
    }
  }

  function downloadCSV() {
    const rows = filtered;
    const header = [
      "submitted_at",
      "rating",
      "audience",
      "industry",
      "health_score",
      "comment",
      "email",
    ];
    const csvRows = rows.map((f) => [
      f.submitted_at || "",
      f.rating ?? "",
      (f.audience || "").replace(/"/g, '""'),
      (f.industry || "").replace(/"/g, '""'),
      f.health_score ?? "",
      (f.comment || "").replace(/[\r\n"]+/g, " ").trim(),
      (f.email || "").replace(/"/g, '""'),
    ]);
    const csv = [header, ...csvRows]
      .map((r) => r.map((cell) => `"${cell}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cfo-grade-feedback-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
              Voice of the user
            </CardDescription>
            <CardTitle className="font-serif-display text-2xl text-[#1F382E]">
              Beta feedback{" "}
              <span className="text-base text-[#5C665D] font-sans font-normal">
                ({fmtNumber(filtered.length)}
                {totalCount && totalCount !== filtered.length ? ` of ${fmtNumber(totalCount)}` : ""}
                {filter !== "all" ? ` · ${filterLabel(filter)}` : ""})
              </span>
            </CardTitle>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="bg-white border border-[#E5E0D8] rounded-md text-xs text-[#1F382E] px-3 py-2"
              data-testid="feedback-filter-select"
            >
              <option value="all">All ratings</option>
              <option value="low">Low (1-2★)</option>
              <option value="with-comment">Has a comment</option>
            </select>
            <Button
              variant="outline"
              size="sm"
              onClick={downloadCSV}
              disabled={filtered.length === 0}
              className="border-[#E5E0D8] text-[#1F382E] hover:bg-[#F7F5F0]"
              data-testid="feedback-csv-btn"
            >
              <Download size={13} strokeWidth={1.5} className="mr-1.5" />
              CSV
            </Button>
            {!allItems && totalCount > (items?.length || 0) && (
              <Button
                size="sm"
                onClick={loadAll}
                disabled={loadingAll}
                className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0]"
                data-testid="feedback-load-all-btn"
              >
                {loadingAll ? "Loading…" : `Load all ${fmtNumber(totalCount)}`}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {filtered.length === 0 ? (
          <p className="text-sm text-[#5C665D]">
            {shown.length === 0
              ? "No feedback yet — once you have testers, it shows up here."
              : `No entries match the "${filterLabel(filter)}" filter.`}
          </p>
        ) : (
          <ul className="space-y-4" data-testid="feedback-list">
            {filtered.map((f) => (
              <li key={f.id} className="border-b border-[#F0EDE5] last:border-b-0 pb-4 last:pb-0">
                <div className="flex items-center gap-3 text-xs text-[#5C665D]">
                  <span className="flex items-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={12}
                        strokeWidth={1.5}
                        className={
                          i < f.rating
                            ? "fill-[#C26D5C] text-[#C26D5C]"
                            : "text-[#D4C9B6]"
                        }
                      />
                    ))}
                  </span>
                  <span>{f.audience || "—"}</span>
                  {f.industry && <span>· {f.industry}</span>}
                  {f.health_score != null && <span>· score {Math.round(f.health_score)}</span>}
                  <span className="ml-auto">
                    {f.submitted_at ? new Date(f.submitted_at).toLocaleString() : ""}
                  </span>
                </div>
                {f.comment && (
                  <p className="text-sm text-[#1A1A1A] mt-2 leading-relaxed">{f.comment}</p>
                )}
                {f.email && (
                  <p className="text-xs text-[#5C665D] mt-1">
                    <a href={`mailto:${f.email}`} className="underline underline-offset-2">
                      {f.email}
                    </a>
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

const PRODUCT_LABELS = {
  pro_30d: "Pro Pass · 30 days",
  pdf_single: "Single PDF unlock",
  cfo_books_preorder: "CFO Books pre-order",
};

// Human-friendly labels for the most-visited paths. Anything not in this map
// just displays the raw path.
const PATH_LABELS = {
  "/": "Landing",
  "/analyze": "Analyze",
  "/report": "Report",
  "/sample-report": "Sample report",
  "/trends": "Trends",
  "/leaderboard": "Leaderboard",
  "/cfo-books": "CFO Books · reservation",
  "/cfo-books/dashboard": "CFO Books · dashboard",
  "/pay/success": "Checkout success",
  "/pay/restore": "Restore unlock",
  "/disclaimer": "Disclaimer",
  "/learn": "Learn hub",
};

function pathLabel(p) {
  if (PATH_LABELS[p]) return PATH_LABELS[p];
  if (p.startsWith("/r/")) return `Reddit landing · ${p.slice(3)}`;
  if (p.startsWith("/for/")) return `Industry · ${p.slice(5)}`;
  if (p.startsWith("/learn/")) return `Concept · ${p.slice(7)}`;
  if (p.startsWith("/leaderboard/")) return `Segment · ${p.slice(13)}`;
  return p;
}

function TrafficPanel({ traffic }) {
  if (!traffic) {
    return (
      <Card className="bg-white border-[#E5E0D8] shadow-none">
        <CardContent className="py-10 text-center text-sm text-[#5C665D]">
          Loading traffic…
        </CardContent>
      </Card>
    );
  }
  const { totals, paths, countries, sources, latest_ts } = traffic;
  const top = paths || [];
  const countryRows = countries || [];
  const sourceRows = sources || [];
  const freshness = latest_ts ? new Date(latest_ts) : null;
  const freshnessLabel = freshness
    ? `Last hit ${timeAgo(freshness)}`
    : "No hits yet";

  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid="traffic-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D] flex items-center gap-2">
              <Eye size={14} strokeWidth={1.5} />
              Website traffic
            </CardDescription>
            <CardTitle className="font-serif-display text-2xl text-[#1F382E] mt-1">
              Who's looking?
            </CardTitle>
          </div>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            {freshnessLabel}
          </span>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatTile
            label="Last 24 hours"
            primary={fmtNumber(totals?.last_24h || 0)}
            sub="pageviews"
            testid="traffic-24h"
          />
          <StatTile
            label="Last 7 days"
            primary={fmtNumber(totals?.last_7d || 0)}
            sub="pageviews"
            testid="traffic-7d"
          />
          <StatTile
            label="All-time"
            primary={fmtNumber(totals?.all_time || 0)}
            sub="pageviews"
            testid="traffic-alltime"
          />
        </div>

        <div className="mt-7">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            Most-visited pages (last 7 days)
          </p>
          {top.length === 0 ? (
            <p className="text-sm text-[#5C665D] mt-3">
              No traffic recorded yet. Hits land here as soon as someone visits a page.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2 mt-3">
              <table className="w-full text-sm" data-testid="traffic-table">
                <thead>
                  <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                    <th className="py-2.5 px-2 font-medium">Page</th>
                    <th className="py-2.5 px-2 font-medium text-right">24h</th>
                    <th className="py-2.5 px-2 font-medium text-right">7d</th>
                    <th className="py-2.5 px-2 font-medium text-right">All-time</th>
                  </tr>
                </thead>
                <tbody>
                  {top.slice(0, 30).map((row) => (
                    <tr
                      key={row.path}
                      className="border-b border-[#F0EDE5] last:border-b-0"
                      data-testid={`traffic-row-${row.path.replace(/[^a-z0-9]+/gi, "-") || "root"}`}
                    >
                      <td className="py-2 px-2">
                        <p className="text-[#1F382E] font-medium">{pathLabel(row.path)}</p>
                        <p className="text-[11px] text-[#5C665D] font-mono">{row.path}</p>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E]">
                        {fmtNumber(row.last_24h)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E] font-medium">
                        {fmtNumber(row.last_7d)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#5C665D]">
                        {fmtNumber(row.all_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* By traffic source */}
        <div className="mt-8" data-testid="traffic-source-section">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            By traffic source (last 7 days)
          </p>
          {sourceRows.length === 0 ? (
            <p className="text-sm text-[#5C665D] mt-3">
              No source data yet. We'll classify each visit as direct, Reddit, Google, Google Ads, etc.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2 mt-3">
              <table className="w-full text-sm" data-testid="traffic-source-table">
                <thead>
                  <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                    <th className="py-2.5 px-2 font-medium">Source</th>
                    <th className="py-2.5 px-2 font-medium text-right">24h</th>
                    <th className="py-2.5 px-2 font-medium text-right">7d</th>
                    <th className="py-2.5 px-2 font-medium text-right">All-time</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceRows.map((row) => (
                    <tr
                      key={row.source || "direct"}
                      className="border-b border-[#F0EDE5] last:border-b-0"
                      data-testid={`traffic-source-row-${(row.source || "direct").replace(/[^a-z0-9]+/gi, "-")}`}
                    >
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2.5">
                          <SourceBadge source={row.source} />
                          <div className="min-w-0">
                            <p className="text-[#1F382E] font-medium">{sourceLabel(row.source)}</p>
                            {sourceSub(row.source) && (
                              <p className="text-[11px] text-[#5C665D] font-mono truncate">
                                {sourceSub(row.source)}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E]">
                        {fmtNumber(row.last_24h)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E] font-medium">
                        {fmtNumber(row.last_7d)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#5C665D]">
                        {fmtNumber(row.all_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <details className="mt-3 text-[12px] text-[#5C665D]" data-testid="source-explainer">
            <summary className="cursor-pointer hover:text-[#1F382E] transition-colors select-none">
              How sources get classified ↓
            </summary>
            <div className="mt-2.5 leading-relaxed space-y-1.5 pl-4 border-l border-[#E5E0D8]">
              <p>
                <strong className="text-[#1F382E]">UTM tags win first.</strong>{" "}
                Tag links with <code className="bg-[#F7F5F0] px-1 rounded">?utm_source=...</code> and
                we'll bucket them: <code className="bg-[#F7F5F0] px-1 rounded">google + cpc</code> → Google Ads,{" "}
                <code className="bg-[#F7F5F0] px-1 rounded">bing + cpc</code> → Bing Ads,{" "}
                <code className="bg-[#F7F5F0] px-1 rounded">reddit + cpc</code> → Reddit Ads,
                anything else → that label verbatim.
              </p>
              <p>
                <strong className="text-[#1F382E]">No UTM?</strong> We read the referrer hostname:
                reddit, google (organic), bing, duckduckgo, twitter/x, linkedin, facebook, instagram,
                youtube, tiktok, hackernews, producthunt. Unknown sites bucket as{" "}
                <code className="bg-[#F7F5F0] px-1 rounded">other:hostname.com</code>.
              </p>
              <p>
                <strong className="text-[#1F382E]">No referrer at all?</strong> → Direct
                (typed URL, bookmark, email client, etc.)
              </p>
            </div>
          </details>
        </div>

        {/* By country */}
        <div className="mt-8" data-testid="traffic-country-section">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            By country (last 7 days)
          </p>
          {countryRows.length === 0 ? (
            <p className="text-sm text-[#5C665D] mt-3">
              No country data yet. We derive country from the visitor's browser timezone — no IP lookup, no cookies.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2 mt-3">
              <table className="w-full text-sm" data-testid="traffic-country-table">
                <thead>
                  <tr className="text-left border-b border-[#E5E0D8] text-[#5C665D]">
                    <th className="py-2.5 px-2 font-medium">Country</th>
                    <th className="py-2.5 px-2 font-medium text-right">24h</th>
                    <th className="py-2.5 px-2 font-medium text-right">7d</th>
                    <th className="py-2.5 px-2 font-medium text-right">All-time</th>
                  </tr>
                </thead>
                <tbody>
                  {countryRows.map((row) => (
                    <tr
                      key={row.country || "ZZ"}
                      className="border-b border-[#F0EDE5] last:border-b-0"
                      data-testid={`traffic-country-row-${row.country || "ZZ"}`}
                    >
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2.5">
                          <span className="text-lg leading-none">{flagEmoji(row.country)}</span>
                          <div>
                            <p className="text-[#1F382E] font-medium">{countryName(row.country)}</p>
                            <p className="text-[11px] text-[#5C665D] font-mono uppercase">
                              {row.country || "ZZ"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E]">
                        {fmtNumber(row.last_24h)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#1F382E] font-medium">
                        {fmtNumber(row.last_7d)}
                      </td>
                      <td className="py-2 px-2 text-right tabular-nums text-[#5C665D]">
                        {fmtNumber(row.all_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Names for the ISO-3166-1 alpha-2 country codes most likely to show up in
// CFO Grade traffic. Anything unknown falls back to the raw code so we can
// still see the breakdown.
const COUNTRY_NAMES = {
  US: "United States",
  CA: "Canada",
  GB: "United Kingdom",
  AU: "Australia",
  NZ: "New Zealand",
  IE: "Ireland",
  IN: "India",
  PK: "Pakistan",
  ZA: "South Africa",
  NG: "Nigeria",
  KE: "Kenya",
  GH: "Ghana",
  DE: "Germany",
  FR: "France",
  ES: "Spain",
  IT: "Italy",
  NL: "Netherlands",
  BE: "Belgium",
  CH: "Switzerland",
  AT: "Austria",
  PL: "Poland",
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IS: "Iceland",
  PT: "Portugal",
  GR: "Greece",
  CZ: "Czechia",
  RO: "Romania",
  HU: "Hungary",
  UA: "Ukraine",
  RU: "Russia",
  TR: "Turkey",
  IL: "Israel",
  AE: "United Arab Emirates",
  SA: "Saudi Arabia",
  EG: "Egypt",
  MX: "Mexico",
  BR: "Brazil",
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  PE: "Peru",
  JP: "Japan",
  KR: "South Korea",
  CN: "China",
  HK: "Hong Kong",
  TW: "Taiwan",
  SG: "Singapore",
  MY: "Malaysia",
  TH: "Thailand",
  VN: "Vietnam",
  ID: "Indonesia",
  PH: "Philippines",
  ZZ: "Unknown",
};

function countryName(code) {
  const c = (code || "ZZ").toUpperCase();
  return COUNTRY_NAMES[c] || c;
}

// Friendly labels + colours for the source bucket the frontend tracker sends.
const SOURCE_META = {
  direct: { label: "Direct / typed URL", color: "#5C665D" },
  reddit: { label: "Reddit (organic)", color: "#FF4500" },
  reddit_ads: { label: "Reddit Ads", color: "#FF4500" },
  google: { label: "Google (organic)", color: "#4285F4" },
  google_ads: { label: "Google Ads", color: "#34A853" },
  bing: { label: "Bing (organic)", color: "#008373" },
  bing_ads: { label: "Bing Ads", color: "#008373" },
  duckduckgo: { label: "DuckDuckGo", color: "#DE5833" },
  twitter: { label: "X (Twitter)", color: "#1A1A1A" },
  linkedin: { label: "LinkedIn", color: "#0A66C2" },
  facebook: { label: "Facebook", color: "#1877F2" },
  facebook_ads: { label: "Facebook Ads", color: "#1877F2" },
  instagram: { label: "Instagram", color: "#E1306C" },
  youtube: { label: "YouTube", color: "#FF0000" },
  tiktok: { label: "TikTok", color: "#1A1A1A" },
  hackernews: { label: "Hacker News", color: "#FF6600" },
  producthunt: { label: "Product Hunt", color: "#DA552F" },
  newsletter: { label: "Newsletter", color: "#9c6b1d" },
  email: { label: "Email", color: "#9c6b1d" },
  podcast: { label: "Podcast", color: "#9c6b1d" },
  partner: { label: "Partner", color: "#2C5C43" },
};

function sourceLabel(source) {
  const s = (source || "direct").toLowerCase();
  if (SOURCE_META[s]) return SOURCE_META[s].label;
  if (s.startsWith("other:")) return "Other referrer";
  // Operator-defined UTM tag — show capitalised
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sourceSub(source) {
  const s = (source || "").toLowerCase();
  if (s.startsWith("other:")) return s.slice(6);
  return null;
}

function SourceBadge({ source }) {
  const s = (source || "direct").toLowerCase();
  const meta = SOURCE_META[s];
  const color = meta?.color || "#5C665D";
  const initial = sourceLabel(source).charAt(0).toUpperCase();
  return (
    <span
      className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[10px] font-semibold tracking-wide text-white shrink-0"
      style={{ backgroundColor: color }}
      aria-hidden="true"
    >
      {initial}
    </span>
  );
}

function flagEmoji(code) {
  const c = (code || "").toUpperCase();
  if (c.length !== 2 || c === "ZZ") return "🌐";
  // Map A-Z to regional-indicator-symbol code points (U+1F1E6 + offset).
  const base = 0x1f1e6;
  const a = "A".charCodeAt(0);
  return String.fromCodePoint(base + (c.charCodeAt(0) - a), base + (c.charCodeAt(1) - a));
}

function timeAgo(d) {
  const s = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const day = Math.floor(h / 24);
  return `${day}d ago`;
}

function PaymentsPanel({ payments, token, onReload }) {
  if (!payments) {
    return (
      <Card className="bg-white border-[#E5E0D8] shadow-none">
        <CardContent className="py-10 text-center text-sm text-[#5C665D]">
          Loading payments…
        </CardContent>
      </Card>
    );
  }

  const live = payments.live_paid || { count: 0, amount: 0 };
  const last30 = payments.last_30d_live || { count: 0, amount: 0 };
  const byProduct = payments.by_product_live || {};
  const recent = payments.recent || [];

  const stripeBadge = (
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#2C5C43] bg-[#2C5C43]/10 border border-[#2C5C43]/30 rounded-full px-2.5 py-1">
      <CheckCircle2 size={11} strokeWidth={2} /> Live
    </span>
  );

  return (
    <Card className="bg-white border-[#E5E0D8] shadow-none" data-testid="payments-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <CardDescription className="text-xs uppercase tracking-[0.18em] text-[#5C665D] flex items-center gap-2">
              <DollarSign size={14} strokeWidth={1.5} />
              Revenue
            </CardDescription>
            <CardTitle className="font-serif-display text-2xl text-[#1F382E] mt-1">
              Payments
            </CardTitle>
          </div>
          {stripeBadge}
        </div>
      </CardHeader>

      <CardContent>
        {/* Headline stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <StatTile
            label="Live revenue · all-time"
            primary={fmtMoney(live.amount)}
            sub={`${live.count} paid`}
            testid="payments-live-alltime"
          />
          <StatTile
            label="Last 30 days · live"
            primary={fmtMoney(last30.amount)}
            sub={`${last30.count} paid`}
            testid="payments-live-30d"
          />
          <StatTile
            label="Pending / abandoned"
            primary={String(
              (payments.all_time_by_status?.pending?.count || 0) +
                (payments.all_time_by_status?.unpaid?.count || 0)
            )}
            sub="checkout opened, not completed"
            muted
            testid="payments-pending"
          />
        </div>

        {/* Per-product breakdown — live only */}
        {Object.keys(byProduct).length > 0 && (
          <div className="mt-7">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
              Live revenue by product
            </p>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {Object.entries(byProduct).map(([pid, info]) => (
                <div
                  key={pid}
                  className="bg-[#F7F5F0] border border-[#E5E0D8] rounded-xl p-4"
                  data-testid={`payments-product-${pid}`}
                >
                  <p className="text-xs text-[#5C665D]">{PRODUCT_LABELS[pid] || pid}</p>
                  <p className="font-serif-display text-2xl text-[#1F382E] tabular-nums mt-1.5">
                    {fmtMoney(info.amount)}
                  </p>
                  <p className="text-[11px] text-[#5C665D] mt-1">{info.count} sold</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent transactions */}
        <div className="mt-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            Recent transactions
          </p>
          {recent.length === 0 ? (
            <p className="text-sm text-[#5C665D] mt-3">
              No transactions yet — once your first user clicks Pay, it will show up here.
            </p>
          ) : (
            <ul className="mt-3 divide-y divide-[#F0EDE5]" data-testid="payments-recent-list">
              {recent.slice(0, 12).map((tx) => (
                <li
                  key={tx.session_id}
                  className="py-3 flex items-center gap-3 text-sm"
                  data-testid={`payments-recent-${tx.session_id}`}
                >
                  <PaymentBadge status={tx.payment_status} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[#1F382E] font-medium truncate">
                      {PRODUCT_LABELS[tx.package_id] || tx.package_id || "—"}
                    </p>
                    <p className="text-[11.5px] text-[#5C665D] truncate">
                      {tx.created_at ? new Date(tx.created_at).toLocaleString() : ""}
                    </p>
                  </div>
                  <p className="font-medium text-[#1F382E] tabular-nums shrink-0">
                    {fmtMoney(tx.amount || 0)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        <ResetTrackerPanel token={token} onReload={onReload} />
      </CardContent>
    </Card>
  );
}

function ResetTrackerPanel({ token, onReload }) {
  const [mode, setMode] = useState("test");
  const [status, setStatus] = useState("any");
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  async function runPreview() {
    setBusy(true);
    setPreview(null);
    try {
      const res = await api.delete("/admin/payments", {
        params: {
          token,
          mode,
          status,
          ...(email.trim() ? { email: email.trim() } : {}),
          dry_run: 1,
        },
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  }

  async function runDelete() {
    if (!preview || !preview.matched) {
      toast.error("Run preview first to confirm what will be deleted.");
      return;
    }
    const ok = window.confirm(
      `This will permanently delete ${preview.matched} payment row(s).\n\n` +
        `Mode: ${mode}\nStatus: ${status}\nEmail: ${email || "—"}\n\n` +
        `This cannot be undone. Continue?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.delete("/admin/payments", {
        params: {
          token,
          mode,
          status,
          ...(email.trim() ? { email: email.trim() } : {}),
          dry_run: 0,
          confirm: "ERASE",
        },
      });
      toast.success(`Deleted ${res.data?.deleted || 0} row(s).`);
      setPreview(null);
      setEmail("");
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="mt-10 rounded-xl border border-[#E5E0D8] bg-[#FAF8F3] p-4 sm:p-5"
      data-testid="payments-reset-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">
            Reset tracker
          </p>
          <p className="font-serif-display text-lg text-[#1F382E] mt-1">
            Selectively wipe seed / test transactions
          </p>
          <p className="text-[12.5px] text-[#5C665D] mt-1 leading-relaxed">
            Always preview first. Live rows are protected by an explicit{" "}
            <code className="bg-white px-1 py-0.5 rounded border border-[#E5E0D8]">mode=live</code>{" "}
            choice plus a confirmation step.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-[#5C665D]">
            Mode
          </Label>
          <select
            value={mode}
            onChange={(e) => { setMode(e.target.value); setPreview(null); }}
            className="mt-1.5 w-full bg-white border border-[#E5E0D8] rounded-md px-3 py-2 text-sm text-[#1F382E]"
            data-testid="reset-mode-select"
          >
            <option value="test">Test only (cs_test_*)</option>
            <option value="live">Live (cs_live_*)</option>
            <option value="any">Any</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-[#5C665D]">
            Status
          </Label>
          <select
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPreview(null); }}
            className="mt-1.5 w-full bg-white border border-[#E5E0D8] rounded-md px-3 py-2 text-sm text-[#1F382E]"
            data-testid="reset-status-select"
          >
            <option value="any">Any status</option>
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="unpaid">Unpaid / abandoned</option>
          </select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-[#5C665D]">
            Email (optional)
          </Label>
          <Input
            value={email}
            onChange={(e) => { setEmail(e.target.value); setPreview(null); }}
            placeholder="you@example.com"
            className="mt-1.5 bg-white border-[#E5E0D8] text-sm"
            data-testid="reset-email-input"
          />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={runPreview}
          disabled={busy}
          data-testid="reset-preview-btn"
          className="border-[#E5E0D8] text-[#1F382E] hover:bg-white"
        >
          {busy ? "Working…" : "Preview matches"}
        </Button>
        <Button
          size="sm"
          onClick={runDelete}
          disabled={busy || !preview || !preview.matched}
          data-testid="reset-delete-btn"
          className="bg-[#8B2C2C] hover:bg-[#751f1f] text-white disabled:opacity-50"
        >
          {preview && preview.matched
            ? `Delete ${preview.matched} row${preview.matched === 1 ? "" : "s"}`
            : "Delete matches"}
        </Button>
        {preview && (
          <span
            className="text-[12px] text-[#5C665D]"
            data-testid="reset-preview-result"
          >
            {preview.matched
              ? `${preview.matched} row(s) match. Click delete to remove.`
              : "No rows match these filters."}
          </span>
        )}
      </div>
    </div>
  );
}

function StatTile({ label, primary, sub, muted, testid }) {
  return (
    <div
      className={`rounded-xl border p-4 ${muted ? "bg-[#F7F5F0] border-[#E5E0D8]" : "bg-white border-[#E5E0D8]"}`}
      data-testid={testid}
    >
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#5C665D]">{label}</p>
      <p className="font-serif-display text-2xl text-[#1F382E] tabular-nums mt-2 leading-none">
        {primary}
      </p>
      {sub && <p className="text-[11px] text-[#5C665D] mt-1.5">{sub}</p>}
    </div>
  );
}

function PaymentBadge({ status }) {
  const map = {
    paid: { label: "Paid", cls: "text-[#2C5C43] bg-[#2C5C43]/10 border-[#2C5C43]/25" },
    pending: { label: "Pending", cls: "text-[#9c6b1d] bg-[#9c6b1d]/10 border-[#9c6b1d]/25" },
    unpaid: { label: "Abandoned", cls: "text-[#5C665D] bg-[#5C665D]/10 border-[#5C665D]/25" },
  };
  const { label, cls } = map[status] || {
    label: status || "—",
    cls: "text-[#5C665D] bg-[#5C665D]/10 border-[#5C665D]/25",
  };
  return (
    <span className={`text-[10px] uppercase tracking-[0.16em] border rounded-full px-2 py-0.5 shrink-0 ${cls}`}>
      {label}
    </span>
  );
}

function fmtMoney(amount) {
  const n = Number(amount || 0);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  }).format(n);
}

function fmtNumber(n) {
  return new Intl.NumberFormat("en-US").format(Number(n || 0));
}

